'use client'

import { Media } from '@plura/shared/types';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@plura/shared/utils';
import './media-display.css';

interface Props {
  media?: Media | null;
  onLoadCallback?: () => void;
  mediOpacity?: number;
  inline?: boolean;
  noTranslate?: boolean;
  highFetchPriority?: boolean;
  mobilePoster?: boolean;
}

const MediaDisplay = ({ media, onLoadCallback, mediOpacity, inline = false, noTranslate = false, highFetchPriority, mobilePoster = false }: Props) => {
  const [loaded, setLoaded] = useState(false);

  // Pre-probe the natural aspect ratio for mobilePoster layouts and track the
  // container's rendered width with ResizeObserver. We then drive the
  // container's height as an explicit pixel value (200px fallback → width /
  // aspect once the probe lands). Both states are real pixel values so the
  // CSS `transition: height` interpolates cleanly — no snap when the image
  // resolves smaller than the 200px reservation.
  const containerRef = useRef<HTMLDivElement>(null);
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!mobilePoster || !media?.link || media?.type === 'video') {
      setNaturalAspect(null);
      return;
    }
    let cancelled = false;
    const probe = new Image();
    probe.onload = () => {
      if (cancelled) return;
      if (probe.naturalWidth && probe.naturalHeight) {
        setNaturalAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    probe.src = `${media.link}?q=70&w=900`;
    return () => {
      cancelled = true;
      probe.onload = null;
    };
  }, [mobilePoster, media?.link, media?.type]);

  useEffect(() => {
    if (!mobilePoster) return;
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setContainerWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [mobilePoster]);

  // Cap the poster height: a portrait image's natural height (width/aspect)
  // can exceed the phone viewport and push the question content below the
  // fold. Past ~1.05× the width (or half the viewport, whichever is smaller)
  // the poster stops growing and object-cover center-crops instead — so
  // landscape/square images keep their exact aspect and portrait ones behave
  // like a poster, not a takeover.
  const posterHeight = mobilePoster && naturalAspect && containerWidth
    ? Math.min(
      containerWidth / naturalAspect,
      containerWidth * 1.05,
      typeof window !== 'undefined' ? window.innerHeight * 0.5 : Number.POSITIVE_INFINITY,
    )
    : null;

  const handleMediaLoad = () => {
    setLoaded(true);
    onLoadCallback?.();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full overflow-hidden',
        // Default (absolute-fill) mode must size to its wrapper — the img is
        // absolutely positioned and contributes no height, so without h-full
        // the container collapses to 0 (broke all form thumbnails). Poster
        // mode drives height via the inline style; inline mode uses h-auto.
        !mobilePoster && !inline && 'h-full',
        inline && !mobilePoster && 'h-auto w-auto overflow-visible',
        inline && !mobilePoster && loaded && 'plura-media-display--loaded',
        inline && !mobilePoster && 'plura-media-display--inline',
        mobilePoster && 'shrink-0 plura-media-display--mobile-poster',
        mobilePoster && posterHeight != null && 'plura-media-display--poster-sized',
      )}
      style={mobilePoster && posterHeight != null ? { height: posterHeight } : undefined}
    >
      <div className={cn('h-full w-full', inline && !mobilePoster && 'plura-media-display-inner')}>
        {
          !media
            ? <span />
            : media?.type === 'video' ? (
              <video
                style={{
                  opacity: loaded
                    ? (mediOpacity !== undefined ? mediOpacity / 100 : 1)
                    : 0
                }}
                src={media?.link}
                controls={false}
                autoPlay
                muted
                playsInline
                loop
                onLoadedData={handleMediaLoad}
                className={cn(
                  'h-full w-full object-cover object-center transition-opacity ease-out [transition-duration:var(--transition-speed,0.5s)]',
                  !mobilePoster && 'absolute left-0 top-0',
                  !mobilePoster && inline && 'relative h-auto w-full',
                )}
              />
            ) : (
              <img
                style={{
                  opacity: loaded
                    ? (mediOpacity !== undefined ? mediOpacity / 100 : 1)
                    : 0
                }}
                src={`${media.link}?q=70&w=900`}
                onLoad={handleMediaLoad}
                className={cn(
                  'h-full w-full object-cover object-center transition-opacity ease-out [transition-duration:var(--transition-speed,0.5s)]',
                  !mobilePoster && 'absolute left-0 top-0',
                  !mobilePoster && inline && 'relative h-auto w-full',
                )}
                fetchPriority={highFetchPriority ? 'high' : 'auto'}
                alt=""
              />
            )
        }
      </div>
    </div>
  );
};

export default MediaDisplay;
